import React, { useEffect, useRef, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, X, Pencil } from "lucide-react";

const TABS = [
  { key: "verticals", label: "Verticals", endpoint: "/master/verticals" },
  { key: "beams", label: "Beams", endpoint: "/master/beams" },
  { key: "panels", label: "Panels", endpoint: "/master/panels" },
];

const schemas = {
  verticals: [
    { k: "section", label: "Section", type: "text" },
    { k: "thickness", label: "Thickness (mm)", type: "number" },
    { k: "strip_width", label: "Strip width (mm)", type: "number" },
    { k: "vertical_required", label: "Qty per rack", type: "number", int: true },
    { k: "bracing_width", label: "Bracing width (mm)", type: "number" },
    { k: "weight_cap_min", label: "Cap. min (kg)", type: "number" },
    { k: "weight_cap_max", label: "Cap. max (kg)", type: "number" },
  ],
  beams: [
    { k: "section", label: "Section", type: "text" },
    { k: "thickness", label: "Thickness (mm)", type: "number" },
    { k: "strip_width", label: "Strip width (mm)", type: "number" },
    { k: "bracket_per_pair_weight", label: "Bracket / pair (kg)", type: "number" },
    { k: "beam_per_level", label: "Beams / level", type: "number", int: true },
    { k: "weight_cap_min", label: "Cap. min (kg)", type: "number" },
    { k: "weight_cap_max", label: "Cap. max (kg)", type: "number" },
  ],
  panels: [
    { k: "size", label: "Panel size (mm)", type: "number" },
    { k: "thickness", label: "Thickness (mm)", type: "number" },
    { k: "strip_width", label: "Strip width (mm)", type: "number" },
    { k: "length_formula", label: "Length formula", type: "text" },
    { k: "weight_cap_min", label: "Cap. min (kg)", type: "number" },
    { k: "weight_cap_max", label: "Cap. max (kg)", type: "number" },
  ],
};

const emptyRow = {
  verticals: {
    section: "", thickness: 0, strip_width: 0, vertical_required: 2,
    bracing_width: 0, weight_cap_min: 0, weight_cap_max: 0,
  },
  beams: {
    section: "", thickness: 0, strip_width: 0, bracket_per_pair_weight: 0,
    beam_per_level: 2, weight_cap_min: 0, weight_cap_max: 0,
  },
  panels: {
    size: 0, thickness: 0, strip_width: 0, length_formula: "depth+50",
    weight_cap_min: 0, weight_cap_max: 0,
  },
};

export default function MasterData() {
  const [tab, setTab] = useState("verticals");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, ...} | null = new
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const current = TABS.find((t) => t.key === tab);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(current.endpoint);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [tab]);

  const save = async () => {
    try {
      // Coerce numbers
      const payload = { ...editing };
      schemas[tab].forEach((f) => {
        if (f.type === "number") {
          payload[f.k] = f.int ? parseInt(payload[f.k]) : parseFloat(payload[f.k]);
        }
      });
      if (editing.id) {
        await api.put(`${current.endpoint}/${editing.id}`, payload);
      } else {
        await api.post(current.endpoint, payload);
      }
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    await api.delete(`${current.endpoint}/${id}`);
    reload();
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/master/upload", fd);
      toast.success(
        `Imported: ${data.counts.verticals} verticals, ${data.counts.beams} beams, ${data.counts.panels} panels`
      );
      reload();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      fileRef.current.value = "";
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin · Master Data"
        title="Section master tables"
        subtitle="Weight capacity ranges drive auto-selection of vertical, beam and panel sections."
        actions={
          <>
            <input
              type="file"
              ref={fileRef}
              accept=".xlsx"
              className="hidden"
              onChange={onUpload}
              data-testid="upload-input"
            />
            <button
              onClick={() => fileRef.current.click()}
              className="btn-secondary"
              data-testid="upload-btn"
            >
              <Upload size={16} /> Upload xlsx
            </button>
            <button
              onClick={() => setEditing({ ...emptyRow[tab] })}
              className="btn-primary"
              data-testid="add-row-btn"
            >
              <Plus size={16} /> Add row
            </button>
          </>
        }
      />

      <div className="p-8">
        <div className="flex items-center gap-px bg-line border border-line w-fit mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-6 py-2 text-sm font-bold uppercase tracking-wider ${
                tab === t.key ? "bg-ink text-white" : "bg-white text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="panel overflow-x-auto">
          <table className="w-full grid-lines">
            <thead>
              <tr>
                {schemas[tab].map((f) => (
                  <th key={f.k} className={f.type === "number" ? "num" : ""}>
                    {f.label}
                  </th>
                ))}
                <th className="w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={99} className="p-6 text-center label-tech">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={99} className="p-6 text-center label-tech">No rows</td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} data-testid={`master-row-${r.id}`}>
                    {schemas[tab].map((f) => (
                      <td key={f.k} className={f.type === "number" ? "num" : ""}>
                        {String(r[f.k] ?? "")}
                      </td>
                    ))}
                    <td>
                      <div className="flex gap-1">
                        <button
                          className="btn-ghost text-xs"
                          onClick={() => setEditing({ ...r })}
                          data-testid={`edit-${r.id}`}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          className="btn-ghost text-xs text-brand-danger"
                          onClick={() => del(r.id)}
                          data-testid={`delete-${r.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center z-50 p-4 no-print">
          <div className="panel-heavy w-full max-w-xl bg-white" data-testid="edit-dialog">
            <div className="px-5 py-4 border-b-2 border-ink flex items-center justify-between">
              <div>
                <div className="label-tech">{editing.id ? "Edit" : "New"}</div>
                <div className="display font-black text-xl uppercase tracking-tighter">
                  {current.label} row
                </div>
              </div>
              <button onClick={() => setEditing(null)} className="btn-ghost">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
              {schemas[tab].map((f) => (
                <div key={f.k} className={f.k === "section" || f.k === "length_formula" ? "col-span-2" : ""}>
                  <label className="label-tech mb-1 block">{f.label}</label>
                  <input
                    className="input-tech mono"
                    type={f.type}
                    value={editing[f.k] ?? ""}
                    onChange={(e) =>
                      setEditing({ ...editing, [f.k]: e.target.value })
                    }
                    data-testid={`field-${f.k}`}
                  />
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={save} className="btn-primary" data-testid="save-row-btn">
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
